import {
  first_truthy,
  normalize_text,
  question_required_value,
  sorted_items,
} from './request_portal_helpers';

const QUESTION_TYPE_PATHS = [
  ['question_type', 'question_type'],
  ['question_type', 'type'],
  ['question_type'],
];

function nested_value(record = {}, path = []) {
  return path.reduce((value, key) => value?.[key], record);
}

function first_path_value(record = {}, paths = []) {
  return paths.map((path) => nested_value(record, path)).find(Boolean);
}

function fallback_main_sections(form_details = {}) {
  if (!Array.isArray(form_details?.sections)) {
    return [];
  }

  return [fallback_main_section(form_details)];
}

function fallback_main_section(form_details = {}) {
  return {
    id: form_details?.id || 'main-section-1',
    main_section_name: form_details?.form_name || 'Case details',
    description: form_details?.form_description || '',
    order_number: 1,
    sub_sections: form_details.sections,
  };
}

function raw_main_sections(form_details = {}) {
  return Array.isArray(form_details?.main_sections)
    ? form_details.main_sections
    : fallback_main_sections(form_details);
}

function normalize_question(question = {}) {
  return normalized_question(question);
}

function normalized_question(question = {}) {
  const choices = question_choices(question);
  return {
    ...question,
    question_text: normalize_text(first_truthy([question?.question_text, 'Untitled question'])),
    question_description: normalize_text(question?.question_description),
    question_type: normalized_question_type(question),
    is_required: question_required_value(question),
    multi_select_choices: Array.isArray(choices) ? sorted_items(choices) : [],
  };
}

function question_choices(question = {}) {
  return first_truthy([question?.multi_select_choices, question?.options]);
}

function normalized_question_type(question = {}) {
  return normalize_text(first_path_value(question, QUESTION_TYPE_PATHS)).toLowerCase();
}

function normalize_sub_section(sub_section = {}) {
  return normalized_sub_section(sub_section);
}

function normalized_sub_section(sub_section = {}) {
  return {
    ...sub_section,
    section_name: normalize_text(first_truthy([sub_section?.section_name, sub_section?.name, ''])),
    description: normalize_text(first_truthy([sub_section.description, sub_section.section_description])),
    questions: sorted_items(first_truthy([sub_section?.questions, []])).map(normalize_question),
  };
}

function normalize_main_section(main_section = {}) {
  return {
    ...main_section,
    main_section_name: normalize_text(main_section?.main_section_name || main_section?.name || 'Section'),
    description: normalize_text(main_section?.description || ''),
    sub_sections: sorted_items(main_section?.sub_sections || []).map(normalize_sub_section),
  };
}

function ordered_sections(form_details = {}) {
  return sorted_items(raw_main_sections(form_details)).map(normalize_main_section);
}

function section_card_from_sub_section(main_section = {}, sub_section = {}) {
  return {
    id: section_card_id(main_section, sub_section),
    name: normalize_text(sub_section?.section_name || main_section?.main_section_name || 'Section'),
    description: normalize_text(sub_section?.description || ''),
    questions: sub_section.questions,
  };
}

function section_card_id(main_section = {}, sub_section = {}) {
  const main_id = main_section.id || main_section.main_section_id || 'main';
  const sub_id = sub_section.id || sub_section.section_id || sub_section.section_name;
  return `${main_id}-${sub_id}`;
}

function visible_sub_sections(main_section = {}) {
  return (Array.isArray(main_section?.sub_sections) ? main_section.sub_sections : [])
    .filter((sub_section) => Array.isArray(sub_section?.questions) && sub_section.questions.length);
}

function section_cards(sections = []) {
  return sections.flatMap((main_section) =>
    visible_sub_sections(main_section).map((sub_section) =>
      section_card_from_sub_section(main_section, sub_section)
    )
  );
}

function can_refresh_open_cases({ loading_open_cases, last_open_cases_refresh_at } = {}) {
  if (loading_open_cases || document.hidden) {
    return false;
  }

  return Date.now() - Number(last_open_cases_refresh_at || 0) >= 3000;
}

export {
  can_refresh_open_cases,
  ordered_sections,
  section_cards,
};
